import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { LogIn, Shield, ArrowLeft, KeyRound, Mail, CheckCircle } from 'lucide-react';
import api from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  // Forgot password states
  const [mode, setMode] = useState('login'); // 'login', 'forgot', 'code', 'success'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post(`${API_URL}/api/admin/auth/forgot-password`, { email: resetEmail });
      setMessage(res.data.message);
      setMode('code');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao solicitar recuperação');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    if (newPassword.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await api.post(`${API_URL}/api/admin/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword
      });
      setMode('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setMode('login');
    setError('');
    setMessage('');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#055509] to-[#0a7a0f] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_hydro-specialist/artifacts/1se2hklr_Verde%20e%20Branco%20Startup%20de%20Soluc%CC%A7o%CC%83es%20Agrono%CC%82micas%20Moderno%20Carta%CC%83o%20de%20Visita%20-%201.jpeg"
              alt="TerraHydro"
              className="h-16 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-[#055509] flex items-center justify-center gap-2">
            {mode === 'login' && <><Shield size={24} />Painel Administrativo</>}
            {mode === 'forgot' && <><Mail size={24} />Recuperar Senha</>}
            {mode === 'code' && <><KeyRound size={24} />Redefinir Senha</>}
            {mode === 'success' && <><CheckCircle size={24} />Senha Redefinida</>}
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {mode === 'login' && 'Acesso restrito para administradores'}
            {mode === 'forgot' && 'Informe seu e-mail cadastrado'}
            {mode === 'code' && 'Digite o código recebido e a nova senha'}
            {mode === 'success' && 'Sua senha foi alterada com sucesso'}
          </p>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && !error && mode === 'code' && (
            <Alert className="mb-4 border-[#055509] bg-green-50">
              <AlertDescription className="text-[#055509]">{message}</AlertDescription>
            </Alert>
          )}

          {/* LOGIN MODE */}
          {mode === 'login' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" required className="h-12" disabled={loading} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Senha</label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-12" disabled={loading} />
              </div>
              <Button data-testid="login-submit" type="submit" className="w-full bg-[#055509] hover:bg-[#0a7a0f] text-white h-12 text-lg font-semibold" disabled={loading}>
                {loading ? (<><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Entrando...</>) : (<><LogIn className="mr-2" size={20} />Entrar no Painel</>)}
              </Button>
              <div className="text-center">
                <button data-testid="forgot-password-link" type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-sm text-[#055509] hover:text-[#FFB025] hover:underline transition-colors font-medium">
                  Esqueci minha senha
                </button>
              </div>
            </form>
          )}

          {/* FORGOT MODE - Enter email */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">E-mail cadastrado</label>
                <Input data-testid="forgot-email" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="seu@email.com" required className="h-12" disabled={loading} />
              </div>
              <Button data-testid="forgot-submit" type="submit" className="w-full bg-[#055509] hover:bg-[#0a7a0f] text-white h-12 font-semibold" disabled={loading}>
                {loading ? 'Enviando...' : 'Gerar Código de Recuperação'}
              </Button>
              <button data-testid="back-to-login" type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-[#055509] transition-colors">
                <ArrowLeft size={16} />Voltar para o login
              </button>
            </form>
          )}

          {/* CODE MODE - Enter code + new password */}
          {mode === 'code' && (
            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Código de Recuperação</label>
                <Input data-testid="reset-code" type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)} placeholder="123456" required className="h-12 text-center text-2xl tracking-widest font-mono" maxLength={6} disabled={loading} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nova Senha</label>
                <Input data-testid="reset-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" required className="h-12" disabled={loading} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmar Nova Senha</label>
                <Input data-testid="reset-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repita a nova senha" required className="h-12" disabled={loading} />
              </div>
              <Button data-testid="reset-submit" type="submit" className="w-full bg-[#055509] hover:bg-[#0a7a0f] text-white h-12 font-semibold" disabled={loading}>
                {loading ? 'Redefinindo...' : 'Redefinir Senha'}
              </Button>
              <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-[#055509] transition-colors">
                <ArrowLeft size={16} />Voltar para o login
              </button>
            </form>
          )}

          {/* SUCCESS MODE */}
          {mode === 'success' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle size={32} className="text-[#055509]" />
              </div>
              <p className="text-gray-600">Sua senha foi redefinida. Agora você pode entrar com a nova senha.</p>
              <Button data-testid="back-to-login-success" onClick={backToLogin} className="w-full bg-[#055509] hover:bg-[#0a7a0f] text-white h-12 font-semibold">
                <LogIn className="mr-2" size={20} />Ir para o Login
              </Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-600">
            <p>TerraHydro Soluções Hidráulicas<br />Sistema de Gerenciamento de Conteúdo</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
